import { TrueFalseChoices } from '@/components/Player/Question/components/Choice/TrueFalse';
import {
  ChoiceDataType,
  ChoiceMetadataType,
  ChoiceOptionType,
  QuestionResponseType,
  TrafficLightType,
  TrueFalsePropType,
} from '@/modules/types/choice';
import {
  QuestionAnswerType,
  QuestionDataType,
  QuestionMetaPropType,
} from '@/modules/types/question';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Bottom from './components/Bottom';
import Choice from './components/Choice';
import Feedback from './components/Feedback';
import Header from './components/Header';

import { ElearningPlayerStateType } from '@/components/Player/modules/reducers/elearningPlayer';
import { QuestionParamType } from '@/pages/Quiz/Questions/routes';
import { useLocation, useParams } from 'react-router-dom';
import { actions as AudioPlayerActions } from '@/components/Player/modules/reducers/audioPlayer';
import { resolveAssetUrl } from '@/lib/util';

interface QuestionPropType {
  question: QuestionDataType;
  meta: QuestionMetaPropType;
  handler: any;
}

const Question = ({
  question,
  handler,
  meta,
}: QuestionPropType): React.ReactElement => {
  const questionTypes: number[] = [
    QuestionResponseType.order,
    QuestionResponseType.match,
  ]; // Dnd options race condition when loading fix
  const [choice, setSelectedAnswer] = useState('');
  const [currentQuestion, setCurrentQuestion] =
    useState<QuestionDataType>(question);
  const [showBottom, setShowBottom] = useState(false);
  const [showFeedBack, setShowFeedBack] = useState<boolean>(false);
  const [choiceList, setChoiceList] = useState<ChoiceOptionType[]>([]);
  const [questionAnswer, setQuestionAnswer] = useState<QuestionAnswerType>({
    isLoaded: false,
    isLoading: false,
    data: null,
  });
  const { elearningId } = useParams<QuestionParamType>();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  /* Preview question link is /preview-quiz/${elearning.data.id}/${questionId}
   * while preview quiz is /preview-quiz/${elearning.data.id}. So if the length of the path
   * is 3, after splitting with "/" and filtering out all the falsy value such as empty string,
   * then it is a preview question else it is a quiz.
   */
  const isPreviewQuestion =
    pathname.includes('preview-quiz') &&
    pathname.split('/').filter(Boolean).length === 3;

  const {
    elearningPlayer: {
      handlers: { submitQuestion },
    },
  }: {
    elearningPlayer: ElearningPlayerStateType;
  } = useSelector(
    (state: { elearningPlayer: ElearningPlayerStateType }) => state,
  );

  const setOptions = (items: any[]) => {
    if (items === undefined) return [];

    const optionLetters = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
    ];
    const result: ChoiceOptionType[] = [];
    items.forEach((obj, index) => {
      const letter = optionLetters.length > index ? optionLetters[index] : '';
      result.push({ option: `${letter}`, data: obj });
    });
    return result;
  };

  const randomItems = (items: ChoiceDataType[]) => {
    if (items === undefined || items.length <= 0) return [];

    const result = [...items];
    result.forEach((val, key) => {
      const randomIndex = Math.round(Math.random() * (items.length - 1));
      result[key] = result[randomIndex];
      result[randomIndex] = val;
    });

    return result;
  };

  const singleOrMultipleCorrectAnswers = (answers: string[] | undefined) => {
    if (!answers || answers.length === 0) return '';

    return answers.length === 1
      ? currentQuestion.choices
          .filter((item: any) => item.id === answers[0])
          .map((answer: any) => answer.content)
      : currentQuestion.choices
          .filter((item: any) => answers.includes(item.id))
          .map((answer: any) => answer.content);
  };

  const getCorrectAnswerSort = () => {
    const dndAnswerSort =
      questionAnswer.data && questionAnswer.data.correctAnswer
        ? [...questionAnswer.data.correctAnswer]
            .sort(
              (a, b) =>
                (a.choiceMetadata?.position || 0) -
                (b.choiceMetadata?.position || 0),
            )

            .map((answer) => answer.content)
        : [];
    return dndAnswerSort;
  };

  const getCorrectAnswerMatch = () => {
    const itemChoices =
      questionAnswer.data && questionAnswer.data.correctAnswer
        ? [...questionAnswer.data.correctAnswer]
            .sort((a, b) =>
              a.choiceMetadata.position !== undefined &&
              b.choiceMetadata.position !== undefined
                ? Number(a.choiceMetadata.position) -
                  Number(b.choiceMetadata.position)
                : 0,
            )
            .filter(
              (itemChoice) =>
                itemChoice.choiceMetadata.type === ChoiceMetadataType.main,
            )
            .map((answer) => answer?.content)
        : [];
    const targetChoices =
      questionAnswer.data && questionAnswer.data.correctAnswer
        ? [...questionAnswer.data.correctAnswer]
            .sort((a, b) =>
              a.choiceMetadata.position !== undefined &&
              b.choiceMetadata.position !== undefined
                ? a.choiceMetadata.position - b.choiceMetadata.position
                : 0,
            )
            .filter(
              (itemChoice) =>
                itemChoice.choiceMetadata.type === ChoiceMetadataType.secondary,
            )
            .map((answer) => answer?.content)
        : [];

    const dndAnswerMatch = itemChoices
      .map((itemChoice, index) => [itemChoice, targetChoices[index]])
      .flat();

    return dndAnswerMatch;
  };

  const getCorrectAnswer = () => {
    const singleOrMultipleAnswer = questionAnswer.data?.correctAnswer?.map(
      (answer: { id: string; position?: number }) => answer.id,
    );

    switch (currentQuestion.questionMetadata.type) {
      case QuestionResponseType.trueOrFalse:
        return currentQuestion.answer ? 'True' : 'False';
      case QuestionResponseType.singleAnswer:
      case QuestionResponseType.multiCorrect:
      case QuestionResponseType.trafficLight:
        return singleOrMultipleCorrectAnswers(singleOrMultipleAnswer);
      case QuestionResponseType.match:
        return getCorrectAnswerMatch();
      case QuestionResponseType.order:
        return getCorrectAnswerSort();
      default:
        return '';
    }
  };

  const handleSubmitAnswer = (answer: string) => {
    setQuestionAnswer({ isLoading: true, isLoaded: false, data: null });
    setSelectedAnswer(answer);
    setShowBottom(true);
    setShowFeedBack(true);
  };

  const getAnswerType = (type: number) => {
    switch (type) {
      case QuestionResponseType.trueOrFalse:
        return { answer: choice };
      case QuestionResponseType.trafficLight:
      case QuestionResponseType.singleAnswer:
      case QuestionResponseType.multiCorrect:
      case QuestionResponseType.order:
        return { multiAnswer: `${choice}`.split(',') };
      case QuestionResponseType.match:
        return { matchKeyPair: JSON.parse(choice) };
      default:
        return { answer: choice };
    }
  };

  useEffect(() => {
    if (choice !== '') {
      const answer = getAnswerType(currentQuestion.questionMetadata.type || 0);
      const submitQuestionCallbackFn = (payload: any) => {
        setQuestionAnswer({
          isLoaded: true,
          isLoading: false,
          data: payload,
        });
      };
      submitQuestion(
        Object.assign(answer, {
          questionId: currentQuestion.id,
          questionMetadata: currentQuestion.questionMetadata,
        }),
        submitQuestionCallbackFn,
      );
    }
  }, [choice]);

  useEffect(() => {
    let choicesItems;
    if (
      currentQuestion.questionMetadata.type &&
      questionTypes.includes(+currentQuestion.questionMetadata.type)
    ) {
      choicesItems = randomItems(currentQuestion.choices);
    } else {
      choicesItems = [...currentQuestion.choices].sort(
        (a, b) =>
          (a.choiceMetadata?.position || 0) - (b.choiceMetadata?.position || 0),
      );
    }

    const optionChoices = setOptions(choicesItems);
    setChoiceList(optionChoices);
  }, [currentQuestion]);

  useEffect(() => {
    if (currentQuestion?.questionMetadata?.sound) {
      dispatch({
        type: AudioPlayerActions.playAudio.type,
        payload: {
          source: resolveAssetUrl({
            assetBasePath: currentQuestion.assetBasePath,
            tenantId: currentQuestion.tenantId,
            entity: 'quizzes',
            entityId: elearningId,
            fileType: 'sounds',
            fileName: currentQuestion.questionMetadata.sound,
          }),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion.id]);

  const handlerSetQuestion = () => {
    setShowBottom(false);
    setShowFeedBack(false);
    setSelectedAnswer('');

    dispatch({
      type: AudioPlayerActions.stopAudio.type,
    });

    if (questionAnswer.data?.nextQuestion !== undefined) {
      setCurrentQuestion(questionAnswer.data.nextQuestion);
      handler();
    } else {
      handler(true);
    }
  };

  const getSelectedChoice = () => {
    const index = choiceList.findIndex(
      ({ data }) => data.lcmsReferenceIds.id === choice || data.id === choice,
    );
    if (index < 0) return undefined;

    const answer = choiceList[index];
    if (
      index < Object.keys(TrafficLightType).length / 2 &&
      currentQuestion.questionMetadata.type ===
        QuestionResponseType.trafficLight
    ) {
      const selectedLightColour = TrafficLightType[index];
      answer.option = `${selectedLightColour}`;
      return answer;
    }

    return answer;
  };

  const getTrueFalseSelectedChoice = () => {
    const selectedChoiceObject = TrueFalseChoices.filter(
      (item: TrueFalsePropType) => `${item.data.value}` === choice,
    );
    return selectedChoiceObject.pop();
  };

  const displayCorrectAnswer = () =>
    meta.showCorrectAnswer ? getCorrectAnswer() : 'Incorrect Answer';

  if (questionAnswer.isLoading) {
    return <div className="loading" />;
  }

  return (
    <div id="replace-content">
      <Header content={currentQuestion.content} />
      <div id="status-bar">
        <div className="steps">
          <span className="sr-only">Question </span>
          <span id="current-no">{meta.currentNo}</span> of
          <span id="total-no"> {meta.total}</span>
        </div>
        <div className="sr-only" id="reader-only-msg" />
      </div>
      <div id="choice-part">
        {!showFeedBack &&
          currentQuestion.questionMetadata.img &&
          !questionTypes.includes(
            currentQuestion.questionMetadata.type || 0,
          ) && (
            <div className="question-img">
              <img
                alt="question"
                src={resolveAssetUrl({
                  assetBasePath: currentQuestion.assetBasePath,
                  tenantId: currentQuestion.tenantId,
                  entity: 'quizzes',
                  entityId: elearningId,
                  fileType: 'images',
                  fileName: currentQuestion.questionMetadata.img,
                })}
              />
            </div>
          )}
        {!showFeedBack && (
          <Choice
            questionType={currentQuestion.questionMetadata.type || 0}
            choiceList={choiceList}
            handler={handleSubmitAnswer}
            isDisabled={showBottom}
          />
        )}
        {/* Need to get correct answer from API and Remove the hardcoded string below */}
        {/* Question type 3 referes to True and False type question */}

        {!!showFeedBack && (
          <Feedback
            questionType={currentQuestion.questionMetadata.type}
            isAnswerCorrect={questionAnswer.data?.isValidatedAnswer}
            selectedAnswer={
              currentQuestion.questionMetadata.type ===
              QuestionResponseType.trueOrFalse
                ? getTrueFalseSelectedChoice()
                : getSelectedChoice()
            }
            // @todo: need to update this logic to not use nested ternary operator
            correctAnswer={
              // eslint-disable-next-line no-nested-ternary
              questionAnswer.data?.isValidatedAnswer
                ? ''
                : displayCorrectAnswer()
            }
            questionFeedback={{
              correct: questionAnswer.data?.feedback.feedback1,
              inCorrect: questionAnswer.data?.feedback.feedback2,
            }}
            showCorrectAnswer={meta.showCorrectAnswer}
            showFeedback={meta.showFeedback}
          />
        )}
      </div>
      {showBottom && !isPreviewQuestion && (
        <Bottom continueHandler={handlerSetQuestion} />
      )}
    </div>
  );
};

export default Question;
